import { CSSProperties, useEffect, useState } from "react";
import { saveAs } from "file-saver";
import { formatBytes } from "core/utils/numberFormat";
import { truncateFilename } from "core/utils/textFormat";
import { GetPaddingStyle, SpacingStyle } from "core/styles/paddingMarginStyle";
import FlexContainer from "../FlexContainer";
import {
  CardStyled,
  TitleCard,
  NameFile,
  SizeFile,
  DeleteIcon,
  TrashIcon,
  SizeFile2,
  DownloadButton,
  DownloadIcon,
} from "./_cardAttachmentStyle";
import { TEST_ID } from "core/constants/test-id";

type Data = {
  id?: number | string;
  name: string;
  size: number | string;
  url: string;
};

type Props = {
  title?: string;
  deleted?: boolean;
  data?: Data;
  handleClickDelete?: (data?: Data) => void;
  iconDelete?: "delete" | "trash";
  truncatedLength?: number;
  type?: "downloader" | "attachment";
  buttonText?: string;
  width?: string;
  "data-testid"?: string;
  style?: CSSProperties;
} & SpacingStyle &
  GetPaddingStyle;

function CardAttachment(props: Props) {
  const {
    title,
    deleted,
    data,
    handleClickDelete,
    iconDelete,
    truncatedLength,
    buttonText,
    type,
    ...rest
  } = props;

  const [isUrlLocal, setUrlLocal] = useState("");

  useEffect(() => {
    if (typeof data?.size === "number") {
      let url = URL.createObjectURL(data);
      setUrlLocal(url);
    }
  }, [data]);

  const generateIconDelete = (icon?: "delete" | "trash") =>
    icon === "trash" ? (
      <TrashIcon
        data-testid={TEST_ID.homeworkExam.deleteUploadedButton}
        onClick={() => handleClickDelete?.(data)}
      />
    ) : (
      <DeleteIcon
        data-testid={TEST_ID.homeworkExam.deleteUploadedButton}
        onClick={() => handleClickDelete?.(data)}
      />
    );

  const truncatedFilenamed = truncateFilename(data?.name, truncatedLength);
  const url = data?.url ? data?.url : isUrlLocal;

  const filesize = () => {
    return typeof data?.size === "string" ? data?.size : formatBytes(data?.size, 2);
  };

  const downloadFile = () => {
    saveAs(url, data?.name);
  };

  let content = null;
  if (type === "attachment") {
    content = (
      <>
        <TitleCard>{title}</TitleCard>
        {data?.name && (
          <>
            <NameFile
              data-testid={TEST_ID.uploader.fileName}
              href={url}
              target="_blank"
              rel="noreferrer">
              {truncatedFilenamed}
            </NameFile>
            {data?.size && <SizeFile>{filesize()}</SizeFile>}
          </>
        )}
        {deleted && generateIconDelete(iconDelete)}
      </>
    );
  } else {
    content = (
      <>
        <FlexContainer justifyContent="space-between">
          <TitleCard>{truncatedFilenamed}</TitleCard>
          <SizeFile2>{filesize()}</SizeFile2>
        </FlexContainer>
        <DownloadButton onClick={downloadFile} variant="secondary" rounded>
          <DownloadIcon />
          {buttonText}
        </DownloadButton>
      </>
    );
  }

  return (
    <CardStyled iconDelete={iconDelete} {...rest}>
      {content}
    </CardStyled>
  );
}

CardAttachment.defaultProps = {
  title: "",
  deleted: false,
  data: {},
  handleClickDelete: () => {},
  iconDelete: "delete",
  truncatedLength: 20,
  type: "attachment",
  buttonText: "Download File",
};

export default CardAttachment;
